import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ModalBackInterceptor — global hardware-back / swipe-back guard.
 *
 * Why: Radix Dialog / Sheet / AlertDialog open via `useState`. They DON'T
 * register a history entry, so on mobile a "back" gesture skips them and
 * jumps the user out of the current page entirely (often back to
 * dashboard). UX-wise that is jarring — users expect the modal to close
 * first.
 *
 * How:
 *   1. On every route change we push a "sentinel" history frame the
 *      first time we observe any open Radix overlay on the page. The
 *      sentinel adds a stack frame the browser can pop without leaving
 *      the SPA route.
 *   2. We listen for `popstate`. If any Radix overlay is currently open
 *      (`[role="dialog"][data-state="open"]` lives in the portal), we
 *      swallow the back press by dispatching an `Escape` keydown that
 *      Radix wires to its own close handler. We then re-push the
 *      sentinel so the NEXT back press repeats the same behaviour for a
 *      stacked modal.
 *   3. If no overlay is open we let `popstate` fall through naturally —
 *      browser back navigates the SPA as expected.
 *
 * This is intentionally side-effect-free for pages that don't open
 * overlays — the sentinel push is lazy and only fires once an overlay
 * is actually mounted.
 */
export default function ModalBackInterceptor() {
  const { pathname } = useLocation();
  const sentinelPushed = useRef(false);

  useEffect(() => {
    // Reset sentinel flag on every navigation. We only push it again
    // when an overlay opens on the new page.
    sentinelPushed.current = false;
  }, [pathname]);

  useEffect(() => {
    const hasOpenOverlay = () => {
      // Radix sets data-state="open" on the portal's <div role="dialog">
      // for Dialog, AlertDialog, AND Sheet (which is built on Dialog).
      // Popover/DropdownMenu also use role="dialog" — but they close on
      // Escape too, so the same handler works.
      return !!document.querySelector('[role="dialog"][data-state="open"]');
    };

    const observer = new MutationObserver(() => {
      if (hasOpenOverlay() && !sentinelPushed.current) {
        try {
          window.history.pushState(
            { __modalSentinel: true, ts: Date.now() },
            ''
          );
          sentinelPushed.current = true;
        } catch (_) {
          /* ignore */
        }
      } else if (!hasOpenOverlay()) {
        // Once the last overlay closes via the X button or programmatic
        // close, the sentinel becomes stale. We don't pop it (that
        // would fire another popstate); we just allow the next overlay
        // to push a fresh one.
        sentinelPushed.current = false;
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });

    const onPopState = () => {
      if (hasOpenOverlay()) {
        // Close the topmost overlay by simulating Escape. Radix handles
        // ESC out of the box for Dialog/Sheet/AlertDialog.
        try {
          document.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: 'Escape',
              code: 'Escape',
              keyCode: 27,
              which: 27,
              bubbles: true,
              cancelable: true,
            })
          );
        } catch (_) {
          /* ignore */
        }
        // Re-arm the sentinel so a second back press also targets the
        // overlay (in case it was a stacked Dialog → Sheet chain).
        try {
          window.history.pushState(
            { __modalSentinel: true, ts: Date.now() },
            ''
          );
          sentinelPushed.current = true;
        } catch (_) {
          /* ignore */
        }
      }
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  return null;
}
